import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AuthControllerService,
  Project,
  ProjectControllerService,
  Task,
  TaskControllerService,
  User,
} from "../../../api";
import {
  FormArray,
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {MatOptionSelectionChange, MatRipple} from "@angular/material/core";
import {TaskCommentsComponent} from "../task-comments/task-comments.component";
import {LphPipe} from "../../../pipes/lph.pipe";
import {Observable, Subject, takeUntil} from "rxjs";
import {CanDeactivateComponent} from "../../../guards/unsaved-changes.guard";
import {createEvent, encodeBase64Utf8} from "./event-download.utils";
import {toLocalDate} from "../../../services/date/local-date";
import {StateConfigService} from "../../../services/state/state-config.service";
import {CdkOverlayOrigin, Overlay, OverlayRef} from "@angular/cdk/overlay";
import {TaskDetailsService} from "../../../services/tasks/task-details.service";
import {MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {MatSelect} from "@angular/material/select";
import {TooltipDirective} from "@buildinginformationcloud/bic-ui-components";
import {PermissionService} from "../../../services/permission/permission.service";

import {
  faCalendarDays,
  faChevronDown,
  faCircle,
  faCircle as faCircleRegular,
  faCircleCheck,
  faCircleHalfStroke,
  faCircleSmall,
  faEllipsisVertical,
  faPaperPlaneTop,
  faUserGroup,
  faUserPlus,
  faX,
  faXmark
} from "@fortawesome/pro-solid-svg-icons";
import {
  BicButtonComponent,
  BicDatepickerInputDirective,
  BicDropdownElementComponent,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  BicTextareaComponent,
  BicToastService,
} from "@buildinginformationcloud/bic-ui-components";
import {MatButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";
import {FaIconComponent, IconDefinition} from "@fortawesome/angular-fontawesome";
import {KeyValuePipe, NgClass} from "@angular/common";
import {PriorityPipe} from "../../../pipes/priority.pipe";
import {TaskStatePipe} from "../../../pipes/task-state.pipe";
import {TitlePipe} from "../../../pipes/title.pipe";
import {HoldableDirective} from "../../../directives/holdable.directive";
import {RoleAndPrivilegesPipe} from "../../../pipes/role-and-privileges.pipe";
import StateEnum = Task.StateEnum;
import PriorityEnum = Task.PriorityEnum;
import LphEnum = Task.LphEnum;
import {NotificationsService} from '../../../services/notifications/notifications.service';

export function shouldDefaultToCurrentUserAssignee(
  hasProjectContext: boolean,
  taskId: string | undefined,
  assignedToUser: User | undefined
): boolean {
  return !hasProjectContext && !taskId && !assignedToUser;
}

export function notBlankValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  return typeof value === 'string' && value.length > 0 && value.trim().length === 0 ? {blank: true} : null;
}

@Component({
  selector: 'app-task-detail',
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,
    ReactiveFormsModule,

    KeyValuePipe,
    PriorityPipe,
    TaskStatePipe,
    TitlePipe,
    TaskCommentsComponent,
    LphPipe,
    BicInputComponent,
    BicDropdownElementComponent,
    BicSelectComponent,
    BicDatepickerInputDirective,
    BicButtonComponent,
    BicPopupComponent,
    BicTextareaComponent,
    NgClass,
    RoleAndPrivilegesPipe,
    TooltipDirective
  ]
})
export class TaskDetailComponent implements OnInit, CanDeactivateComponent, OnChanges, OnDestroy {
  readonly taskTitleMaxLength = 180;
  lphPipe = inject(LphPipe);
  stateService = inject(StateConfigService);
  overlay = inject(Overlay);
  data = inject<Task>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<TaskDetailComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private taskControllerService = inject(TaskControllerService);
  private formBuilder = inject(UntypedFormBuilder);
  private projectControllerService = inject(ProjectControllerService);
  private authControllerService = inject(AuthControllerService);
  private taskDetailsService = inject(TaskDetailsService);
  notificationsService = inject(NotificationsService);
  permissionService = inject(PermissionService);
  private toastService = inject(BicToastService);

  @ViewChild('userSelection') userSelection!: MatSelect | undefined;

  @Input() overlayOrigin: CdkOverlayOrigin | undefined;
  @Input() openDetails: boolean = false;

  projects: Project[] = []

  @Input() taskId!: string;
  @Output() taskChange: EventEmitter<Task> = new EventEmitter<Task>();

  taskForm!: UntypedFormGroup;

  user!: User;
  users: User[] = [];
  selectedUser!: User;

  project!: Project;
  today: Date = new Date();

  create: boolean = false;
  // Sperrt Speichern, Abbrechen, × und Escape, solange der PUT läuft (Doppelklick, #2736).
  isSubmitting = false;
  currentDate: Date = new Date();

  usersObservable!: Observable<User[]> | undefined;

  informedUsersObservable!: Observable<User[]>;

  informedUsers: User[] = [];

  stateEnum: typeof StateEnum = StateEnum;

  priorityEnum: typeof PriorityEnum = PriorityEnum;

  @ViewChild('comments') commentsComponent: TaskCommentsComponent | undefined;

  @ViewChild('overlayTemplate') overlayTemplate!: TemplateRef<any>;
  overlayRef!: OverlayRef | null;

  @ViewChildren(BicDatepickerInputDirective) datePicker!: QueryList<BicDatepickerInputDirective>;

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.taskChange.emit(this.taskForm.value);
    this.taskDetailsService.closeAndSaveTask(this.taskForm.value)
    this.datePicker.forEach(datePicker => {
      datePicker.datepicker?.hide();
    });
  }

  disableForm(): boolean {
    return this.taskForm.disabled;
  }

  validForm(): boolean {
    return this.taskForm.valid || this.taskForm.disabled;
  }

  canDeactivate(): boolean {
    return this.taskForm.pristine;
  }


  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // this.init();
  }

  /** Comparator für das Verantwortlich-Select: leerer Wert (null/undefined) passt zu keinem User. */
  compare(a: User | null | undefined, b: User | null | undefined): boolean {
    return !!a && !!b && a.id === b.id;
  }




  init() {
    this.currentDate = new Date();

    this.taskForm = this.formBuilder.group({
      id: new UntypedFormControl(),
      title: new UntypedFormControl('', [Validators.required, notBlankValidator, Validators.maxLength(this.taskTitleMaxLength)]),
      description: new UntypedFormControl(),
      state: new UntypedFormControl(StateEnum.OPEN, Validators.required),
      priority: new UntypedFormControl(PriorityEnum.NORMAL),
      lph: new UntypedFormControl(),
      dueDate: new UntypedFormControl(),
      informedUsers: new UntypedFormControl([]),
      assignedToUser: new UntypedFormControl(),
      creationUser: new UntypedFormControl(),
      modifiedUser: new UntypedFormControl(),
      project: new UntypedFormControl(),
      projectID: new UntypedFormControl(),
      comment: this.formBuilder.array([]),
    });

    this.taskForm.get('title')?.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value: string | null) => {
      if (typeof value === 'string' && value.length > this.taskTitleMaxLength) {
        this.taskForm.get('title')?.setValue(value.slice(0, this.taskTitleMaxLength), {emitEvent: false});
      }
    });

    if (history.state.project && history.state.lph && history.state.service) {
      this.taskForm.get("project")!.setValue(history.state.project);
      this.taskForm.get("project")!.disable();
      this.taskForm.get("state")!.setValue(this.stateEnum.OPEN);
      this.taskForm.get("description")!.setValue("Leistungsphase " + this.lphPipe.transform(history.state.lph));
    }

    if(this.data) {
      const commentControl = this.taskForm.get('comment') as FormArray;
      if (this.data.comment) {
        this.data.comment.forEach((comment) => {
          commentControl.push(this.createCommentGroup(comment));
        });
      }
    }

    this.taskForm.get("projectID")?.setValue(this.stateService.getProjectId());

    this.taskForm.patchValue(this.taskDetailsService.getOpenedTask() ?? this.taskForm.value);

    // Sync projectID from task data (Task model uses "projectId", form uses "projectID")
    if (!this.taskForm.get("projectID")?.value && this.data?.projectId) {
      this.taskForm.get("projectID")?.setValue(this.data.projectId);
    }

    this.project = this.stateService.getProject()!;
    this.user = this.stateService.user;

    const projectId = this.taskForm.get("projectID")?.value || this.data?.projectId || this.stateService.getProject()?.id;
    this.users = this.taskDetailsService.getCachedUsers(projectId ?? -1);
    if (this.users.length === 0 && projectId) {
      this.taskDetailsService.loadProjectUsers(projectId).pipe(takeUntil(this.unsubscribe)).subscribe(users => {
        this.users = users;
      });
    }

    const hasProjectContext = !!projectId;
    if (shouldDefaultToCurrentUserAssignee(
      hasProjectContext,
      this.taskForm.get('id')?.value,
      this.taskForm.get('assignedToUser')?.value
    )) {
      this.taskForm.get('assignedToUser')?.setValue(this.user);
      if (!this.users.some(user => user.id === this.user.id)) {
        this.users = [this.user, ...this.users];
      }
    }


    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === "Escape") {
        this.close();
      }
    });
    this.informedUsers = this.taskForm.get('informedUsers')?.value ?? [];

    if (!this.permissionService.hasProjectPrivilege('PROJECT_TASK_EDIT')) {
      this.taskForm.disable({emitEvent: false});
    }
  }

  formatDateForInput(date: string): string {
    const parsedDate = new Date(date);
    return parsedDate.toISOString();
  }


  isSelected(user: User): boolean {
    return this.informedUsers.some((u: User) => u.id === user.id);
  }


  parseDateString(dateString: string): Date {
    const [day, month, year] = dateString.split('.').map(Number);
    return new Date(year, month - 1, day);
  }

  save() {
    if (this.isSubmitting) {
      return;
    }

    if (!this.permissionService.hasProjectPrivilege('PROJECT_TASK_EDIT')) {
      return;
    }

    this.taskForm.markAllAsTouched();
    if (this.taskForm.invalid) {
      return;
    }

    if(!this.task.id){
      this.taskForm.get('creationUser')?.setValue(this.user);
    } else {
      this.taskForm.get('modifiedUser')?.setValue(this.user);
    }

    this.taskForm.get('title')?.setValue(this.taskForm.get('title')?.value.trim(), {emitEvent: false});

    // Request inkl. Kalender-Payload zuerst bauen: wirft hier etwas, bleibt der Dialog offen und
    // speicherbar, weil isSubmitting noch nicht gesetzt ist (#2645).
    const projectId = this.taskForm.get('projectID')?.value;
    const request$ = this.taskControllerService.updateTask(this.taskForm.value, this.createEventForCal(), projectId ?? -1);
    const isNew = !this.taskForm.value.id;

    this.isSubmitting = true;
    // Während des PUT nichts mehr bearbeitbar lassen: spätere Eingaben wären beim Schließen verloren (#2736).
    const projectWasDisabled = !!this.taskForm.get('project')?.disabled;
    this.taskForm.disable({emitEvent: false});

    // Kein takeUntil: die Antwort soll auch verarbeitet werden, falls der Dialog vorher zerstört wurde.
    request$.subscribe({
      next: saved => {
        this.taskDetailsService.currentTaskChanged.next(saved);
        if (isNew) {
          this.toastService.success('Aufgabe erstellt.');
        }
        // isSubmitting bleibt gesetzt, damit während der Schließ-Animation kein weiterer Klick durchkommt.
        this.dialogRef.close();
      },
      error: () => {
        this.isSubmitting = false;
        this.unlockFormAfterFailedSave(projectWasDisabled);
        this.notificationsService.showErrorMessage("Die Aufgabe konnte nicht gespeichert werden.");
      }
    });
  }

  private unlockFormAfterFailedSave(projectWasDisabled: boolean) {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_TASK_EDIT')) {
      return;
    }
    this.taskForm.enable({emitEvent: false});
    // Das über history.state vorbelegte Projekt bleibt gesperrt.
    if (projectWasDisabled) {
      this.taskForm.get('project')?.disable({emitEvent: false});
    }
  }

  saveFromComment(event: any) {
    const index = event.index;
    const deleted = event.deleted;
    if(this.taskForm.get('id')?.value) {
      this.taskControllerService.getTask(this.taskForm.get('id')?.value).subscribe((task: Task) => {
        let comments = this.taskForm.get('comment') as UntypedFormArray;
        if (deleted) {
          task.comment?.splice(index, 1);
        } else {
          let comment = comments.value[index];
          if (task.comment) {
            task.comment[index] = comment
          } else {
            task.comment = [comment]
          }
        }
        const projectId = this.taskForm.get('projectID')?.value;
        this.taskControllerService.updateTask(task, this.createEventForCal(), projectId ?? -1).subscribe(updatedTask => {
          // @ts-ignore
          comments.at(index).setValue(updatedTask.comment[index])
          this.taskDetailsService.currentTaskChanged.next(this.taskForm.value)

        })
      })
    }
  }

  close() {
    // Während des Speicherns kein Abbrechen vortäuschen, das gar nicht passiert (#2736).
    if (this.isSubmitting) {
      return;
    }
    this.taskDetailsService.clearOpenedTask();
    this.dialogRef.close();
  }

  createEventForCal() {
    // Nicht new Date: das liest einen getippten Feldtext „05.10.2026“ als 10.05.2026.
    const deadline = toLocalDate(this.taskForm.get('dueDate')?.value);
    if (!deadline) {
      return "";
    }
    let events = [{
      deadline,
      summary: 'Deadline Aufgabe: ' + this.taskForm.get('title')?.value,
      description: 'Fertigstellung der Aufgabe: ' + this.taskForm.get('title')?.value,
      url: 'https://www.thinkbic.de/'
    }]
    let content = createEvent(events)
    return encodeBase64Utf8(content)
  }


  get task(): Task {
    return this.taskForm.value
  }


  reload() {
    this.taskControllerService.getComments(this.task.id!).subscribe(comments => {
      // this.taskForm.patchValue({ comments: comments });
      comments?.forEach(comment => {
        this.commentsComponent?.addCommentFrom(comment);
      })
    });
  }

  keepOrder = (a: any, b: any) => {
    return a;
  }

  reverse = (a: any, b: any) => {
    return 1;
  }

  openDeleteDialog() {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_TASK_DELETE')) {
      return;
    }
    let dialogRef = this.dialog.open(TaskDeleteDialog, {
      panelClass: "rounded-corners-dialog",
      data: {task: this.task}
    });

    dialogRef.afterClosed().subscribe(result => {
      this.close();
    });

  }

  resetUsers(project: Project) {
    if (project) {
      this.usersObservable = this.projectControllerService.getUsers(project.id!);
      this.taskForm.controls.users.enable()
    } else {
      this.usersObservable = undefined;
      this.taskForm.patchValue({users: this.user})
      this.userSelection?.focus();
    }

  }

  unsubscribe: Subject<void> = new Subject<void>();


  /**
   * Set assignedTo User
   * @param user
   * @param event
   */
  setSelectedUser(user: User, event: MatOptionSelectionChange) {
    if (event.isUserInput) {
      this.selectedUser = user;
      for (let i = 0; i < this.taskForm.controls.informedUsers.value.length; i++) {
        if (this.taskForm.controls.informedUsers.value[i].id === user.id) {
          this.taskForm.controls.informedUsers.setValue(this.taskForm.controls.informedUsers.value.filter((u: User) => u.id !== user.id));
          return;
        }
      }
    }
  }

  toggleCheckbox(event: Event, user: User): void {
    event.preventDefault();
    if (this.informedUsers.some((u: User) => u.id === user.id)) {
      this.deselectUser(user);
    } else {
      this.selectUser(user);
    }
  }



  selectUser(user: User): void {
    this.informedUsers.push(user);
    this.taskForm.get('informedUsers')?.setValue(this.informedUsers);

    // let informedUsers = this.taskForm.get('informedUsers')?.value;
    // console.log("HIER")
    // console.log(this.taskForm.get('informedUsers') as UntypedFormArray)
    // console.log(informedUsers)
    // console.log("HIER ENDE")
    // if (!Array.isArray(informedUsers)) {
    //   informedUsers = [];
    // }
    // let test = [];
    // for (let i = 0; i < informedUsers.length; i++) {
    //   test.push(informedUsers[i]);
    // }
    // console.log(test)
    //
    //
    // if (!informedUsers.some((u: User) => u.id === user.id)) {
    //   informedUsers = [...informedUsers, user];
    // }
    // this.taskForm.get('informedUsers')?.setValue(informedUsers);
    // console.log(this.taskForm.get('informedUsers')?.value);
  }

  deselectUser(user: User): void {
    this.informedUsers = this.informedUsers.filter((u: User) => u.id !== user.id);
    this.taskForm.get('informedUsers')?.setValue(this.informedUsers);
  }
  getRemainingUserNames(): string {
    if (!this.informedUsers || this.informedUsers.length <= 3) return '';
    return this.informedUsers
      .slice(3)
      .map(u => `${u.firstName} ${u.lastName}`)
      .join(', ');
  }

  private createCommentGroup(comment:any): FormGroup {
    return this.formBuilder.group({
      atGuid: new UntypedFormControl(),
      date: new UntypedFormControl(),
      author: new UntypedFormControl(),
      comment: new UntypedFormControl(),
      viewpoint: new UntypedFormControl(),
      modifiedDate: new UntypedFormControl(),
      modifiedAuthor: new UntypedFormControl(),
      authorUser: new UntypedFormControl(),
      modifiedAuthorUser: new UntypedFormControl(),
    });
  }

  protected readonly faXmark = faXmark;

  protected readonly Task = Task;
  protected readonly LphEnum = LphEnum;

  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faX = faX;
  protected readonly faPaperPlaneTop = faPaperPlaneTop;
  protected readonly faCalendarDays = faCalendarDays;
  protected readonly faUserPlus = faUserPlus;
  protected readonly faUserGroup = faUserGroup;
  protected readonly faCircleHalfStroke = faCircleHalfStroke;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircle = faCircle;
  protected readonly faCircleRegular = faCircleRegular;
  protected readonly faCircleSmall = faCircleSmall;

  getStatusIcon(state: string): IconDefinition {
    switch (state) {
      case this.stateEnum.OPEN:
        return this.faCircleRegular;
      case this.stateEnum.PROCESS:
        return this.faCircleHalfStroke;
      case this.stateEnum.DONE:
        return this.faCircle;
      case this.stateEnum.VERIFIED:
        return this.faCircleCheck;
      default:
        return this.faCircleRegular;
    }
  }

  protected readonly faChevronDown = faChevronDown;
}


@Component({
  selector: 'app-task-deleteDialog',
  templateUrl: './task-delete-dialog.component.html',
  imports: [
    MatDialogContent,
    MatButton,
    MatTooltip,
    HoldableDirective,
    MatDialogClose,
    HoldableDirective
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./task-detail.component.scss']
})

//DEPRECATED
export class TaskDeleteDialog implements OnInit {
  data = inject<{
    task: Task;
  }>(MAT_DIALOG_DATA);
  private router = inject(Router);
  private taskControllerService = inject(TaskControllerService);
  private dialogRef = inject<MatDialogRef<TaskDeleteDialog>>(MatDialogRef);
  private taskDetailsService = inject(TaskDetailsService);

  @ViewChild(MatRipple) ripple!: MatRipple;

  holdHandler(e: any) {
    if (e === 3000) {
      this.dialogRef.close();
      this.taskControllerService.deleteTask(this.data.task.id!).subscribe(next => {
        // @ts-ignore
        this.taskDetailsService.currentTaskDeleted.next(this.data.task);
      });
    }
  }

  ngOnInit() {
  }




}
